/**
 * Progress Bar Service
 *
 * Usage (matches Swing API):
 * var progress = ProgressBar.init("Description", 100, true);
 * ProgressBar.show(progress.getId());
 * ProgressBar.get(progress.getId()).inc();
 * ProgressBar.get(progress.getId()).close();
 */

export interface ProgressBarOptions {
  description: string
  max?: number // -1 for indeterminate, undefined defaults to -1
  cancelable?: boolean
  onCancel?: () => void
  headlineProgress?: boolean // Show "current / max" format
}

export interface ProgressBarInstance {
  id: string
  description: string
  current: number
  max: number // -1 means indeterminate
  cancelled: boolean
  cancelable: boolean
  headlineProgress: boolean
  onCancel?: () => void
  visible: boolean
}

class ProgressBarService {
  public instances: Map<string, ProgressBarInstance> = new Map()
  private listeners: Map<string, Set<(instance: ProgressBarInstance) => void>> = new Map()
  private nextId = 0
  private lastId: string | null = null

  /**
   * Initialize a progress bar (indeterminate)
   */
  init(description: string): ProgressBarInstance
  /**
   * Initialize a progress bar with max value
   */
  init(description: string, max: number): ProgressBarInstance
  /**
   * Initialize a progress bar with max and cancelable
   */
  init(description: string, max: number, cancelable: boolean): ProgressBarInstance
  /**
   * Initialize a progress bar with options object
   */
  init(options: ProgressBarOptions): ProgressBarInstance
  init(
    descriptionOrOptions: string | ProgressBarOptions,
    max?: number,
    cancelable?: boolean
  ): ProgressBarInstance {
    let options: ProgressBarOptions

    if (typeof descriptionOrOptions === 'string') {
      options = {
        description: descriptionOrOptions,
        max: max !== undefined ? max : -1, // Default to indeterminate
        cancelable: cancelable ?? false
      }
    } else {
      options = {
        description: descriptionOrOptions.description,
        max: descriptionOrOptions.max ?? -1,
        cancelable: descriptionOrOptions.cancelable ?? false,
        onCancel: descriptionOrOptions.onCancel,
        headlineProgress: descriptionOrOptions.headlineProgress ?? true
      }
    }

    const id = `progress-${++this.nextId}-${Date.now()}`
    const instance: ProgressBarInstance = {
      id,
      description: options.description,
      current: 0,
      max: options.max ?? -1,
      cancelled: false,
      cancelable: options.cancelable ?? false,
      headlineProgress: options.headlineProgress ?? true,
      onCancel: options.onCancel,
      visible: false
    }

    this.instances.set(id, instance)
    this.lastId = id
    return instance
  }

  /**
   * Show the progress bar
   */
  show(id: string): void {
    const instance = this.instances.get(id)
    if (!instance) {
      throw new Error(`Progress bar with id ${id} not found`)
    }

    instance.visible = true
    // Notify listeners that progress bar should be shown
    this.notifyListeners(id, instance)
  }

  /**
   * Increment progress bar
   * Only increments if current < max (and max >= 0)
   */
  inc(id: string): void {
    const instance = this.instances.get(id)
    if (!instance) {
      return
    }

    // Only increment if not indeterminate and not at max
    if (instance.max >= 0 && instance.current < instance.max) {
      instance.current = instance.current + 1
      this.notifyListeners(id, instance)
    }
  }

  /**
   * Set progress bar value
   */
  setValue(id: string, value: number): void {
    const instance = this.instances.get(id)
    if (!instance) {
      return
    }

    instance.current = Math.max(0, Math.min(value, instance.max))
    this.notifyListeners(id, instance)
  }

  /**
   * Check if progress bar is cancelled
   */
  isCanceled(id: string): boolean {
    const instance = this.instances.get(id)
    return instance?.cancelled ?? false
  }

  /**
   * Cancel the progress bar
   */
  cancel(id: string): void {
    const instance = this.instances.get(id)
    if (!instance) {
      return
    }

    instance.cancelled = true

    if (instance.onCancel) {
      try {
        instance.onCancel()
      } catch (error) {
        console.error('Error in onCancel callback:', error)
      }
    }

    this.notifyListeners(id, instance)
  }

  /**
   * Close the progress bar
   */
  close(id: string): void {
    const instance = this.instances.get(id)
    if (instance) {
      instance.visible = false
      instance.cancelled = true
      this.notifyListeners(id, instance)
    }

    this.instances.delete(id)
    this.listeners.delete(id)
  }

  /**
   * Get progress bar instance
   */
  get(id: string): ProgressBarInstance | undefined {
    return this.instances.get(id)
  }

  /**
   * Get most recently created progress bar instance
   */
  getLastInstance(): ProgressBarInstance | undefined {
    if (!this.lastId) {
      return undefined
    }
    return this.instances.get(this.lastId)
  }

  /**
   * Get progress bar instance
   */
  getInstance(id: string): ProgressBarInstance | undefined {
    return this.get(id)
  }

  /**
   * Set onCancel callback
   */
  setOnCancel(id: string, callback: () => void): void {
    const instance = this.instances.get(id)
    if (instance) {
      instance.onCancel = callback
    }
  }

  /**
   * Set headline progress mode
   */
  setHeadlineProgress(id: string, headlineProgress: boolean): void {
    const instance = this.instances.get(id)
    if (instance) {
      instance.headlineProgress = headlineProgress
      this.notifyListeners(id, instance)
    }
  }

  /**
   * Subscribe to progress bar updates
   */
  subscribe(id: string, callback: (instance: ProgressBarInstance) => void): () => void {
    if (!this.listeners.has(id)) {
      this.listeners.set(id, new Set())
    }

    this.listeners.get(id)!.add(callback)

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(id)
      if (listeners) {
        listeners.delete(callback)
        if (listeners.size === 0) {
          this.listeners.delete(id)
        }
      }
    }
  }

  private notifyListeners(id: string, instance: ProgressBarInstance): void {
    const listeners = this.listeners.get(id)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(instance)
        } catch (error) {
          console.error('Error in progress bar listener:', error)
        }
      })
    }
  }
}

export const progressBarService = new ProgressBarService()

